import { useEffect, useRef } from "react";
import { Alert, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, FoodsTags, MRTGrid, Typography } from "@foodpilot/foods";
import type { MRT_ColumnDef, MRT_Row } from "material-react-table";
import { useTranslation } from "react-i18next";
import type { TemplateAction } from "src/api/client-api/actionPlanTemplates/useActionPlanTemplate";
import { useTemplateActionCatalog } from "src/api/client-api/actionPlanTemplates/useTemplateActionCatalog";

type Props = {
    actions: TemplateAction[];
    ssqVersionId: number;
    busy: boolean;
    onAdd: () => void;
    onEdit: (action: TemplateAction) => void;
    onRemove: (actionId: number) => void;
    onMove: (fromIndex: number, toIndex: number) => void;
};

const DragHandleIcon = () => <FoodsIcon icon="drag" size={2} />;
const KEYBOARD_MOVE_STEPS: Partial<Record<string, number>> = { ArrowUp: -1, ArrowDown: 1 };

type ActionRow = TemplateAction & {
    title: string;
    categories: { id: number; title: string }[];
};

export const TemplateEditableActions = (props: Props) => {
    const { actions, ssqVersionId, busy, onAdd, onEdit, onRemove, onMove } = props;

    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const dragHandles = useRef(new Map<number, HTMLButtonElement>());
    const keyboardMovedActionId = useRef<number | null>(null);
    const moveRow = (fromIndex: number, toIndex: number) => {
        if (busy || fromIndex === toIndex || toIndex < 0 || toIndex >= actions.length) return false;
        onMove(fromIndex, toIndex);
        return true;
    };

    useEffect(() => {
        if (keyboardMovedActionId.current === null) return;
        dragHandles.current.get(keyboardMovedActionId.current)?.focus();
        keyboardMovedActionId.current = null;
    }, [actions]);
    const catalog = useTemplateActionCatalog(
        actions.map((action) => action.actionId),
        ssqVersionId,
    );
    const rows: ActionRow[] = actions.map((action) => ({
        ...action,
        title:
            catalog.data?.get(action.actionId)?.title ??
            t("settings.action_plan_templates.detail.action_unavailable", "Action unavailable"),
        categories: [
            ...new Map(
                (catalog.data?.get(action.actionId)?.csrThemes ?? []).map((theme) => {
                    const category = theme.parent ?? theme;

                    return [category.id, category] as const;
                }),
            ).values(),
        ].sort((a, b) => a.title.localeCompare(b.title, i18n.resolvedLanguage)),
    }));

    return (
        <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={theme.spacing(2)}>
                <Stack direction="row" alignItems="center" gap={theme.spacing(2)}>
                    <Typography variant="h3">
                        {t("settings.action_plan_templates.detail.added_actions", "Added actions")}
                    </Typography>
                    <FoodsTags label={String(actions.length)} />
                </Stack>
                <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() => onAdd()}
                    startIcon={<FoodsIcon icon="plus" size={1} />}
                >
                    {t("settings.action_plan_templates.editor.add_action", "Add an action")}
                </Button>
            </Stack>
            {catalog.isError && (
                <Alert severity="error">
                    {t(
                        "settings.action_plan_templates.detail.catalog_failed",
                        "Unable to load action details. Please try again.",
                    )}
                    <Button variant="text" onClick={() => catalog.refetch()}>
                        {t("settings.action_plan_templates.retry", "Try again")}
                    </Button>
                </Alert>
            )}
            <MRTGrid<ActionRow>
                columns={
                    [
                        {
                            accessorKey: "title",
                            header: t("settings.action_plan_templates.detail.action_name", "Action name"),
                        },
                        {
                            id: "category",
                            accessorFn: (row) => row.categories.map((category) => category.title).join(", "),
                            header: t("settings.action_plan_templates.detail.category", "Category"),
                            Cell: ({ row }) => (
                                <Stack direction="row" flexWrap="wrap" gap={theme.spacing(1)}>
                                    {row.original.categories.length ?
                                        row.original.categories.map((category) => (
                                            <FoodsTags
                                                key={`${category.id}-template-action-category`}
                                                label={category.title}
                                                colors={{
                                                    background: theme.custom.grey[400],
                                                    foreground: theme.custom.grey[2500],
                                                }}
                                            />
                                        ))
                                    :   <Typography>—</Typography>}
                                </Stack>
                            ),
                        },
                    ] satisfies MRT_ColumnDef<ActionRow>[]
                }
                data={rows}
                getRowId={(row) => `${row.actionId}-template-edit-row`}
                enableRowNumbers
                enableSorting={false}
                enableRowOrdering
                icons={{ DragHandleIcon }}
                displayColumnDefOptions={{ "mrt-row-drag": { header: "" } }}
                muiRowDragHandleProps={({ row, table }) => ({
                    title: t("settings.action_plan_templates.editor.move_action", "Move {{name}}", {
                        name: row.original.title,
                    }),
                    onDragEnd: (event) => {
                        const { draggingRow, hoveredRow } = table.getState();
                        if (
                            event.dataTransfer.dropEffect !== "none" &&
                            draggingRow &&
                            hoveredRow?.index !== undefined
                        ) {
                            moveRow(draggingRow.index, hoveredRow.index);
                        }
                    },
                    onKeyDown: (event) => {
                        const step = KEYBOARD_MOVE_STEPS[event.key];
                        if (step === undefined || event.altKey || event.metaKey || event.ctrlKey) return;
                        event.preventDefault();
                        event.stopPropagation();
                        if (moveRow(row.index, row.index + step)) {
                            keyboardMovedActionId.current = row.original.actionId;
                        }
                    },
                    ref: (element: HTMLButtonElement | null) => {
                        if (element) {
                            dragHandles.current.set(row.original.actionId, element);
                        } else {
                            dragHandles.current.delete(row.original.actionId);
                        }
                    },
                })}
                enableRowActions
                positionActionsColumn="last"
                enableRowSelection={false}
                enableColumnActions={false}
                enableColumnFilters={false}
                enableTopToolbar={false}
                enableBottomToolbar={false}
                enablePagination={false}
                state={{ isLoading: actions.length > 0 && catalog.isLoading }}
                renderRowActions={({ row }: { row: MRT_Row<ActionRow> }) => (
                    <Stack direction="row">
                        <Button
                            variant="text"
                            disabled={busy}
                            sx={{ color: "text.primary" }}
                            aria-label={t("settings.action_plan_templates.editor.edit_action", "Edit {{name}}", {
                                name: row.original.title,
                            })}
                            onClick={() => onEdit(row.original)}
                        >
                            <FoodsIcon icon="edit" size={1.5} />
                        </Button>
                        <Button
                            variant="text"
                            disabled={busy}
                            sx={{ color: "text.primary" }}
                            aria-label={t("settings.action_plan_templates.editor.remove_action", "Remove {{name}}", {
                                name: row.original.title,
                            })}
                            onClick={() => onRemove(row.original.actionId)}
                        >
                            <FoodsIcon icon="delete" size={1.5} />
                        </Button>
                    </Stack>
                )}
            />
        </>
    );
};
